import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { api } from '../services/api';
import { PlusIcon, PencilIcon, TrashIcon, UserGroupIcon, MagnifyingGlassIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner, Button, Modal, Table, Card, Input, ConfirmModal } from '../components/ui';
import { PageHeader, DataToolbar, StatusBadge, ActionIconButton, DataTableCard } from '../components/ui';
import toast from 'react-hot-toast';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'MANAGER' | 'EMPLOYEE';
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
}

interface UserFormData {
  name: string;
  email: string;
  password?: string;
  role: 'ADMIN' | 'MANAGER' | 'EMPLOYEE';
}

const UserModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
  onSave: (data: UserFormData) => void;
}> = ({ isOpen, onClose, user, onSave }) => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm<UserFormData>({
    defaultValues: user ? {
      name: user.name,
      email: user.email,
      role: user.role
    } : {
      name: '',
      email: '',
      password: '',
      role: 'EMPLOYEE'
    }
  });

  React.useEffect(() => {
    if (user) {
      reset({
        name: user.name,
        email: user.email,
        role: user.role
      });
    } else {
      reset({
        name: '',
        email: '',
        password: '',
        role: 'EMPLOYEE'
      });
    }
  }, [user, reset]);

  const onSubmit = (data: UserFormData) => {
    onSave(data);
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose}
      title={user ? 'Editar Usuario' : 'Nuevo Usuario'}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre Completo *
          </label>
          <input
            type="text"
            {...register('name', { required: 'El nombre es requerido', minLength: { value: 2, message: 'Mínimo 2 caracteres' } })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Juan Pérez"
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            {...register('email', { required: 'El email es requerido', pattern: { value: /^\S+@\S+$/i, message: 'Email inválido' } })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="usuario@funeraria.com"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
        </div>

        {!user && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contraseña *
            </label>
            <input
              type="password"
              {...register('password', { required: !user ? 'La contraseña es requerida' : false, minLength: { value: 6, message: 'Mínimo 6 caracteres' } })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="••••••••"
            />
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rol *
          </label>
          <select
            {...register('role', { required: 'El rol es requerido' })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="EMPLOYEE">Empleado</option>
            <option value="MANAGER">Gerente</option>
            <option value="ADMIN">Administrador</option>
          </select>
          {errors.role && <p className="text-red-500 text-sm mt-1">{errors.role.message}</p>}
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            onClick={handleClose}
            variant="outline"
          >
            Cancelar
          </Button>
          <Button type="submit">
            {user ? 'Actualizar' : 'Crear Usuario'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const Users: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {}
  });

  const queryClient = useQueryClient();

  const { data: users, isLoading } = useQuery<{ data: { data: User[] } }>(
    'users',
    () => api.get('/users')
  );

  const createMutation = useMutation(
    (userData: UserFormData) => api.post('/users', userData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('Usuario creado exitosamente');
        setIsModalOpen(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error al crear usuario');
      }
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: UserFormData }) => api.put(`/users/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('Usuario actualizado exitosamente');
        setIsModalOpen(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error al actualizar usuario');
      }
    }
  );

  const deleteMutation = useMutation(
    (id: string) => api.delete(`/users/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('Usuario eliminado exitosamente');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error al eliminar usuario');
      }
    }
  );

  const toggleStatusMutation = useMutation(
    (id: string) => api.put(`/users/${id}/toggle-status`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('Estado del usuario actualizado');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error al cambiar estado');
      }
    }
  );

  const handleCreate = () => {
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleSave = (data: UserFormData) => {
    if (selectedUser) {
      updateMutation.mutate({ id: selectedUser.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (user: User) => {
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Usuario',
      message: `¿Está seguro de que desea eliminar al usuario ${user.name}? Esta acción no se puede deshacer.`,
      type: 'danger',
      onConfirm: () => {
        deleteMutation.mutate(user.id);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleToggleStatus = (user: User) => {
    setConfirmModal({
      isOpen: true,
      title: user.isActive ? 'Desactivar Usuario' : 'Activar Usuario',
      message: `¿Está seguro de que desea ${user.isActive ? 'desactivar' : 'activar'} al usuario ${user.name}?`,
      type: 'warning',
      onConfirm: () => {
        toggleStatusMutation.mutate(user.id);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const getRoleBadge = (role: string) => {
    // Colores institucionales sobrios y profesionales
    const colors = {
      ADMIN: 'bg-slate-700 text-white',
      MANAGER: 'bg-slate-500 text-white',
      EMPLOYEE: 'bg-emerald-100 text-emerald-800'
    };
    const labels = {
      ADMIN: 'Administrador',
      MANAGER: 'Gerente',
      EMPLOYEE: 'Empleado'
    };
    return (
      <span className={`px-2.5 py-1 rounded-md text-xs font-medium ${colors[role as keyof typeof colors]}`}>
        {labels[role as keyof typeof labels]}
      </span>
    );
  };

  const filteredUsers = users?.data?.data?.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const columns = [
    {
      key: 'name',
      label: 'Usuario',
      render: (_: any, user: User) => (
        <div>
          <p className="font-medium text-slate-900">{user.name}</p>
          <p className="text-sm text-slate-500 mt-0.5">{user.email}</p>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Rol',
      render: (_: any, user: User) => getRoleBadge(user.role)
    },
    {
      key: 'isActive',
      label: 'Estado',
      render: (_: any, user: User) => <StatusBadge tone={user.isActive ? 'success' : 'neutral'}>{user.isActive ? 'Activo' : 'Inactivo'}</StatusBadge>
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_: any, user: User) => (
        <div className="flex items-center space-x-1">
          <ActionIconButton title="Editar usuario" onClick={() => handleEdit(user)}>
            <PencilIcon className="h-4 w-4" strokeWidth={2} />
          </ActionIconButton>
          <ActionIconButton title={user.isActive ? 'Desactivar usuario' : 'Activar usuario'} onClick={() => handleToggleStatus(user)} tone={user.isActive ? 'danger' : 'success'}>
            {user.isActive ? (
              <LockClosedIcon className="h-4 w-4" strokeWidth={2} />
            ) : (
              <LockOpenIcon className="h-4 w-4" strokeWidth={2} />
            )}
          </ActionIconButton>
          <ActionIconButton title="Eliminar usuario" onClick={() => handleDelete(user)} tone="danger">
            <TrashIcon className="h-4 w-4" strokeWidth={2} />
          </ActionIconButton>
        </div>
      )
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Gestión de Usuarios"
        subtitle={`Administra accesos y roles. ${filteredUsers.length} usuario(s) visible(s).`}
        actions={
          <Button onClick={handleCreate} className="bg-slate-800 hover:bg-slate-900 text-white">
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo Usuario
          </Button>
        }
      />

      {/* Search - Integrado visualmente */}
      <DataToolbar searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Buscar por nombre o email..." />

      {/* Table - Mejor legibilidad y espaciado */}
      <DataTableCard>
        <Table
          data={filteredUsers}
          columns={columns}
          loading={isLoading}
          emptyMessage="No se encontraron usuarios"
          className="border-0 shadow-none"
          rowClassName="hover:bg-slate-50"
        />
      </DataTableCard>

      {/* Modals */}
      <UserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        user={selectedUser}
        onSave={handleSave}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
      />
    </div>
  );
};

export default Users;

